import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    Box,
    debounce,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    useMediaQuery
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store/store";
import {useAuth} from "../../context/useAuth";
import {MaiSplitPage} from "../../components/splitPage/MaiSplitPage";
import {fetchUiSettingsPreview, getAgentBasic, getAgentUiSettings, getSnippet, putAgentUiSettings} from "../../services/DataService";
import {AgentUiSettingsDto} from "../../dto/AgentUiSettingsDto";
import type {AgentBasicDto} from "../../dto/AgentBasicDto";
import {setSnackbarOpen} from "../../store/slices/snackbar.slice";
import Typography from "@mui/material/Typography";
import {ColorInput} from "../../components/ColorInput/ColorInput";
import Button from "@mui/material/Button";
import {ConfirmSaveDialog} from "../../components/confirmSaveDialog/ConfirmSaveDialog";
import {setCancel, setConfirm, setDiscard, setUnsavedChanges} from "../../store/slices/changes.slice";
import theme from "../../theme/theme";
import {SidebarSvg} from '../../components/svg/SidebarSvg';
import {PopoutSvg} from '../../components/svg/PopoutSvg';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {handleApiCall} from "../../utils/handleApiCall";
import {isValidUrl} from "../../utils/validation";
import {TextFieldWithHeader} from "../../components/textField/TextFieldWithHeader";
import ToggleTabs from "../../components/tabs/ToggleTabs";
import {setActionButton} from "../../store/slices/layout.slice";
import {setActionButtonCallback} from "../../utils/actionButtonHandler";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

enum tabs {
    LiveAnsicht,
    Design,
    Veroeffentlichen
}

function CustomTabPanel(props: TabPanelProps) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

export function LiveViewPage() {
    const {token, logout} = useAuth();
    const dispatch = useDispatch();
    const {selectedAgent} = useSelector((state: RootState) => state.agents);
    const {unsavedChanges, confirm, discard, cancel} = useSelector((state: RootState) => state.changes);
    const [value, setValue] = useState(0);
    const [newValue, setNewValue] = useState(0);
    const [open, setOpen] = useState(false);
    const [snippet, setSnippet] = useState<string>("")
    const tabLabels = ['Liveansicht', 'Design', 'Veröffentlichen'];
    const [uiSettings, setUiSettings] = useState<AgentUiSettingsDto | null>(null);
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [logoError, setLogoError] = React.useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isReady = selectedAgent && token;
    const [agentBasic, setAgentBasic] = useState<AgentBasicDto | null>(null);
    const [loadingAgentBasic, setLoadingAgentBasic] = useState(false);

    // Agent-Info laden für die Prüfung auf Messenger-Aktivierung
    useEffect(() => {
        const load = async () => {
            if (!isReady) return;
            setLoadingAgentBasic(true);
            try {
                const res = await getAgentBasic(selectedAgent.gcpAgentId, token);
                setAgentBasic(res.data as AgentBasicDto);
            } catch (error: any) {
                if (error?.response?.status === 401) {
                    logout();
                } else {
                    dispatch(setSnackbarOpen({snackbarMessage: 'Fehler beim Laden des Agenten.', snackbarSeverity: 'error'}));
                }
            } finally {
                setLoadingAgentBasic(false);
            }
        };
        load();
    }, [selectedAgent, token]);

    const updateUiSettings = (newSettings: Partial<AgentUiSettingsDto>) => {
        setUiSettings(prev => ({...prev!, ...newSettings}));
        dispatch(setUnsavedChanges(true));
    };

    const debouncedPreview = useCallback(
        debounce((settings: AgentUiSettingsDto) => {
            if (!isReady) return;
            refreshPreview(selectedAgent.gcpAgentId, token, settings);
        }, 300), // 300 Millisekunden warten
        [selectedAgent, token]
    );

    async function refreshPreview(agentId: string, token: string, settings: AgentUiSettingsDto) {
        await handleApiCall(
            () => fetchUiSettingsPreview(agentId, token, settings),
            {
                onSuccess: (response) => setPreviewHtml(response.data),
                errorMessage: "Preview konnte nicht geladen werden.",
                logout,
                dispatch,
            }
        );
    }

    async function fetchUiSettings(agentId: string, token: string) {
        await handleApiCall(
            () => getAgentUiSettings(agentId, token),
            {
                onSuccess: (response) => {
                    const loadedSettings = response.data;
                    if (!loadedSettings.displayMode) {
                        loadedSettings.displayMode = 'sidebar';
                    }
                    setUiSettings(loadedSettings);
                },
                logout,
                dispatch,
            }
        );
    }

    async function saveUiSettings(agentId: string, token: string) {
        if (!uiSettings) return;

        const isValid = isValidUrl(uiSettings.logoUrl);
        setLogoError(!isValid);
        if (!isValid) return;

        await handleApiCall(
            () => putAgentUiSettings(agentId, uiSettings, token),
            {
                successMessage: "Design gespeichert!",
                errorMessage: "Design speichern fehlgeschlagen.",
                onSuccess: () => dispatch(setUnsavedChanges(false)),
                logout,
                dispatch,
            }
        );
    }

    async function getLiveSnippet(token: string) {
        await handleApiCall(
            () => getSnippet(selectedAgent?.gcpAgentId ?? "", token),
            {
                onSuccess: (response) => setSnippet(response.data),
                errorMessage: "Snippet konnte nicht geladen werden.",
                logout,
                dispatch,
            }
        );
    }


    const updateValue = () => {
        if (newValue !== value) {
            setValue(newValue);
        }
    }

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setNewValue(newValue);
        if (unsavedChanges) {
            setOpen(true);
        } else {
            setValue(newValue);
        }
    };

    const handleSaveDesign = () => {
        if (!isReady) return;
        saveUiSettings(selectedAgent.gcpAgentId, token);
    }
    const handleSaveDesignRef = useRef(handleSaveDesign);
    useEffect(() => {
        handleSaveDesignRef.current = handleSaveDesign;
    }, [handleSaveDesign]);

    useEffect(() => {
        if (value === tabs.Design) {
            dispatch(setActionButton({
                label: 'Speichern',
                disabled: !unsavedChanges,
                hidden: false,
            }));
            setActionButtonCallback(() => handleSaveDesignRef.current());
        }

        return () => {
            dispatch(setActionButton(null));
            setActionButtonCallback(null);
        };
    }, [value, unsavedChanges]);

    const handleDialogSave = () => {
        setOpen(false);
        handleSaveDesign();
        updateValue();
    }

    const handleClose = () => {
        setOpen(false);
    };

    const handleDiscard = () => {
        setOpen(false);
        resetFormValues();
        updateValue();
    };

    const resetFormValues = () => {
        if (!isReady) return;
        fetchUiSettings(selectedAgent.gcpAgentId, token);
        dispatch(setUnsavedChanges(false));
    }

    const resetDesignDefaults = () => {
        if (!uiSettings) return;
        updateUiSettings({
            ...uiSettings,
            fontFamily: "Google Sans",
            chatBackgroundColor: "#f3f6fc",
            chatBubbleBorderRadius: 0,
            logoUrl: "",
            title: "",
            titleFontColor: "#000",
            messageUserBackgroundColor: "#d3e3fd",
            messageUserFontColor: "#000",
            messageBotBackgroundColor: "#fff",
            messageBotFontColor: "#000",
            chatWidth: 320,
            chatHeight: 480,
            chatBorderColor: "#111",
            chatBorderWidth: 1,
            windowBackgroundColor:"#f3f6fc"

            // Optional: Weitere Standardwerte können hier ebenfalls zurückgesetzt werden
        });
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(snippet).then(() => {
            dispatch(setSnackbarOpen({snackbarMessage: "In die Zwischenablage kopiert.", snackbarSeverity: 'success'}));
        });
    };

    useEffect(() => {
        if (!isReady) return;
        getLiveSnippet(token);
    }, [selectedAgent, token, value]);

    useEffect(() => {
        if (!isReady && !uiSettings) return;
        if (uiSettings) {
            debouncedPreview(uiSettings);
        }
    }, [uiSettings]);

    useEffect(() => {
        if (!isReady) return;
        fetchUiSettings(selectedAgent.gcpAgentId, token);
    }, [selectedAgent, token]);

    useEffect(() => {
        if (confirm) {
            dispatch(setConfirm(false));
            handleDialogSave();
        }
    }, [confirm]);

    useEffect(() => {
        if (discard) {
            dispatch(setDiscard(false));
            handleDiscard();
        }
    }, [discard]);

    useEffect(() => {
        if (cancel) {
            dispatch(setCancel(false));
            handleClose();
        }
    }, [cancel]);

    const isMessengerEnabled = !loadingAgentBasic && agentBasic && agentBasic.messengerEnabled;
    if (!isMessengerEnabled) {
        return (
            <Box p={3}>
                <Typography variant="body1">
                    <p>Der Chatbot ist noch nicht aktiviert.</p>
                    <p>Die Aktivierung wird durch unser Team vorgenommen. Sobald diese abgeschlossen ist, benachrichtigen wir Sie umgehend. Vielen Dank für Ihre Geduld.</p>
                </Typography>
            </Box>
        );
    }

    const leftPage = (
        <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        }}>
            <Box pt={2} sx={{ flexShrink: 0 }}>
                <ToggleTabs
                    value={value}
                    onChange={handleChange}
                    tabLabels={tabLabels}
                />
            </Box>
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <CustomTabPanel value={value} index={tabs.LiveAnsicht}>
                    {snippet !== "" && (
                        <Box
                            p={2}
                            sx={{
                                height: '700px',
                                overflow: 'hidden',
                            }}
                        >
                            <iframe title={"Live Chat"} width={'100%'} height={'100%'} srcDoc={snippet}
                                    style={{border: 0}}></iframe>
                        </Box>
                    )}
                </CustomTabPanel>
                <CustomTabPanel value={value} index={tabs.Design}>
                    {uiSettings && (
                        <Box p={2} sx={{display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row'}}>
                            <Box
                                component="form"
                                p={2}
                                sx={{
                                    flex: 1, '& > :not(style)': {mb: 2},
                                    overflowY: 'auto'
                                }}
                                noValidate
                                autoComplete="off"
                            >
                                <Box display={"flex"} justifyContent={"space-between"} flexWrap={"wrap"}>
                                    <Typography variant="h6" component={"h5"}>Benutzeroberfläche des Chatbots anpassen</Typography>
                                    <Button
                                        disableRipple
                                        variant="outlined"
                                        onClick={resetDesignDefaults}
                                        sx={{
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Design zurücksetzen
                                    </Button>
                                </Box>

                                <Typography variant="h6" sx={{mt: 4}}>Grundeinstellungen</Typography>

                                <Box display={"flex"} gap={2}>
                                    <TextFieldWithHeader
                                        fullWidth
                                        header="Breite"
                                        unit="px"
                                        type="number"
                                        value={uiSettings.chatWidth}
                                        onChange={(e) => updateUiSettings({
                                            ...uiSettings,
                                            chatWidth: parseInt(e.target.value)
                                        })}
                                    />

                                    <TextFieldWithHeader
                                        fullWidth
                                        header="Höhe"
                                        unit="px"
                                        type="number"
                                        value={uiSettings.chatHeight}
                                        onChange={(e) => updateUiSettings({
                                            ...uiSettings,
                                            chatHeight: parseInt(e.target.value)
                                        })}
                                    />
                                </Box>

                                <TextFieldWithHeader
                                    fullWidth
                                    select
                                    header="Schriftart"
                                    value={uiSettings.fontFamily}
                                    onChange={(e) => updateUiSettings({...uiSettings, fontFamily: e.target.value})}
                                >
                                    <MenuItem value="Google Sans">Google Sans</MenuItem>
                                    <MenuItem value="Arial">Arial</MenuItem>
                                    <MenuItem value="Roboto">Roboto</MenuItem>
                                    <MenuItem value="serif">serif</MenuItem>
                                    <MenuItem value="sans-serif">sans-serif</MenuItem>
                                </TextFieldWithHeader>

                                <ColorInput
                                    label="Hintergrundfarbe Chat"
                                    value={uiSettings.chatBackgroundColor}
                                    onChange={(value) => updateUiSettings({...uiSettings, chatBackgroundColor: value})}
                                />

                                <ColorInput
                                    label="Hintergrundfarbe Eingabefeld"
                                    value={uiSettings.windowBackgroundColor}
                                    onChange={(value) => updateUiSettings({...uiSettings, windowBackgroundColor: value})}
                                />

                                <TextFieldWithHeader
                                    header="Bubble Radius"
                                    unit="px"
                                    type="number"
                                    value={uiSettings.chatBubbleBorderRadius}
                                    onChange={(e) => updateUiSettings({
                                        ...uiSettings,
                                        chatBubbleBorderRadius: parseInt(e.target.value)
                                    })}
                                />

                                <Typography variant="h6" sx={{mt: 4}}>Rahmen</Typography>
                                <ColorInput
                                    label="Rahmenfarbe"
                                    value={uiSettings.chatBorderColor}
                                    onChange={(value) => updateUiSettings({...uiSettings, chatBorderColor: value})}
                                />

                                <Box display={"flex"} gap={2}>
                                    <TextFieldWithHeader
                                        fullWidth
                                        header="Rahmenbreite"
                                        unit="px"
                                        type="number"
                                        value={uiSettings.chatBorderWidth}
                                        onChange={(e) => updateUiSettings({
                                            ...uiSettings,
                                            chatBorderWidth: parseInt(e.target.value)
                                        })}
                                    />

                                </Box>



                                <Typography variant="h6" sx={{mt: 4}}>Titelleiste</Typography>

                                <TextFieldWithHeader
                                    fullWidth
                                    error={logoError}
                                    helperText={logoError ? "Bitte geben Sie eine gültige Adresse ein." : ""}
                                    color={logoError ? 'error' : 'primary'}
                                    header="Logo-URL"
                                    value={uiSettings.logoUrl}
                                    onChange={(e) => {
                                        updateUiSettings({...uiSettings, logoUrl: e.target.value})
                                    }
                                    }
                                />

                                <TextFieldWithHeader
                                    fullWidth
                                    header="Titel"
                                    value={uiSettings.title}
                                    onChange={(e) => updateUiSettings({...uiSettings, title: e.target.value})}
                                />

                                <ColorInput
                                    label="Titel-Farbe"
                                    value={uiSettings.titleFontColor}
                                    onChange={(value) => updateUiSettings({...uiSettings, titleFontColor: value})}
                                />

                                <Typography variant="h6" sx={{mt: 4}}>Benutzereingabe</Typography>

                                <ColorInput
                                    label="User-Hintergrundfarbe"
                                    value={uiSettings.messageUserBackgroundColor}
                                    onChange={(value) => updateUiSettings({
                                        ...uiSettings,
                                        messageUserBackgroundColor: value
                                    })}
                                />
                                <ColorInput
                                    label="User-Textfarbe"
                                    value={uiSettings.messageUserFontColor}
                                    onChange={(value) => updateUiSettings({...uiSettings, messageUserFontColor: value})}
                                />

                                <Typography variant="h6" sx={{mt: 4}}>Antwort des Agenten</Typography>

                                <ColorInput
                                    label="Bot-Hintergrundfarbe"
                                    value={uiSettings.messageBotBackgroundColor}
                                    onChange={(value) => updateUiSettings({
                                        ...uiSettings,
                                        messageBotBackgroundColor: value
                                    })}
                                />
                                <ColorInput
                                    label="Bot-Textfarbe"
                                    value={uiSettings.messageBotFontColor}
                                    onChange={(value) => updateUiSettings({...uiSettings, messageBotFontColor: value})}
                                />

                                <FormControl component="fieldset">
                                    <Typography variant="h6" sx={{mt: 4}}>Darstellungsmodus</Typography>
                                    <RadioGroup
                                        row
                                        value={uiSettings.displayMode}
                                        onChange={(e) =>
                                            updateUiSettings({
                                                ...uiSettings,
                                                displayMode: e.target.value as 'popout' | 'sidebar',
                                            })
                                        }
                                    >
                                        <FormControlLabel
                                            value="popout"
                                            control={<Radio/>}
                                            label={
                                                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                                    <PopoutSvg/>
                                                    <Typography>Popout</Typography>
                                                </Box>
                                            }
                                        />
                                        <FormControlLabel
                                            value="sidebar"
                                            control={<Radio/>}
                                            label={
                                                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                                    <SidebarSvg/>
                                                    <Typography>Sidebar</Typography>
                                                </Box>
                                            }
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>

                            {previewHtml !== "" && (
                                <Box
                                    sx={{
                                        width: isMobile ? '100%' : '50%',
                                        height: '700px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <iframe title={"Live Chat"} width={'400px'} height={'100%'} srcDoc={previewHtml}
                                            style={{border: 1}}></iframe>
                                </Box>
                            )
                            }

                        </Box>
                    )}
                </CustomTabPanel>
                <CustomTabPanel value={value} index={tabs.Veroeffentlichen}>
                    <Box p={2}>
                        <Typography gutterBottom>
                            Füge diesen Agenten zu deiner Webseite hinzu, indem du den folgenden Code kopierst:
                        </Typography>
                        <Box sx={{position: 'relative', display: 'inline-block'}}>
                            <Paper
                                elevation={3}
                                sx={{
                                    bgcolor: theme.palette.custom.customBackgroundGray,
                                    p: 2,
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    borderRadius: 2,
                                    minWidth: '300px'
                                }}
                            >
                                {snippet}
                            </Paper>

                            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
                                <IconButton
                                    onClick={handleCopy}
                                >
                                    <ContentCopyIcon/>
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                </CustomTabPanel>
            </Box>

            <ConfirmSaveDialog
                open={open}
                onConfirm={handleDialogSave}
                onDiscard={handleDiscard}
                onCancel={handleClose}
            />
        </Box>
    );
    
    return (
        <MaiSplitPage
            singlePage={true}
            childrenLeft={leftPage}
        />
    )
}
